import { useMemo } from 'react';
import Button from 'devextreme-react/button';
import { previewFileUrl } from '../fileBrowser/fileBrowserApi';
import type { MediaGalleryItem } from './mediaGalleryPlaylist';
import {
  playlistIndexOf,
  playlistNext,
  playlistPrev
} from './mediaGalleryPlaylist';
import type { GalleryViewMode, ThumbSize } from './viewerOpenPreference';
import './MediaGalleryPane.css';

const THUMB_SIZE_PX: Record<ThumbSize, number> = {
  small: 48,
  medium: 72,
  large: 96
};

export interface MediaGalleryPaneProps {
  mountId: string;
  relativePath: string;
  playlist: readonly MediaGalleryItem[];
  viewMode: GalleryViewMode;
  thumbSize: ThumbSize;
  onSelectPath: (path: string) => void;
  onViewModeChange: (mode: GalleryViewMode) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  zoomFit: () => void;
  panZoomEnabled: boolean;
}

export function MediaGalleryPane({
  mountId,
  relativePath,
  playlist,
  viewMode,
  thumbSize,
  onSelectPath,
  onViewModeChange,
  zoomIn,
  zoomOut,
  zoomFit,
  panZoomEnabled
}: MediaGalleryPaneProps) {
  const currentIndex = playlistIndexOf(playlist, relativePath);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < playlist.length - 1;
  const thumbPx = THUMB_SIZE_PX[thumbSize];

  const neighbors = useMemo(() => {
    if (playlist.length <= 1) return playlist;
    const start = Math.max(0, currentIndex - 5);
    const end = Math.min(playlist.length, currentIndex + 6);
    return playlist.slice(start, end);
  }, [playlist, currentIndex]);

  const goPrev = () => {
    const item = playlistPrev(playlist, relativePath);
    if (item) onSelectPath(item.path);
  };

  const goNext = () => {
    const item = playlistNext(playlist, relativePath);
    if (item) onSelectPath(item.path);
  };

  if (playlist.length <= 1) {
    return (
      <div className="media-gallery-pane media-gallery-pane--single">
        {panZoomEnabled ? (
          <div className="media-gallery-pane__zoom" role="toolbar" aria-label="Image zoom">
            <Button icon="minus" stylingMode="text" hint="Zoom out" onClick={zoomOut} />
            <Button icon="plus" stylingMode="text" hint="Zoom in" onClick={zoomIn} />
            <Button icon="fullscreen" stylingMode="text" hint="Fit to view" onClick={zoomFit} />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="media-gallery-pane">
      <div className="media-gallery-pane__nav" role="toolbar" aria-label="Gallery navigation">
        <Button
          icon="chevronprev"
          stylingMode="text"
          hint="Previous image"
          disabled={!hasPrev}
          onClick={goPrev}
        />
        <span className="media-gallery-pane__counter" aria-live="polite">
          {currentIndex >= 0 ? `${currentIndex + 1} / ${playlist.length}` : `— / ${playlist.length}`}
        </span>
        <Button
          icon="chevronnext"
          stylingMode="text"
          hint="Next image"
          disabled={!hasNext}
          onClick={goNext}
        />
        {panZoomEnabled ? (
          <>
            <span className="media-gallery-pane__sep" aria-hidden="true" />
            <Button icon="minus" stylingMode="text" hint="Zoom out" onClick={zoomOut} />
            <Button icon="plus" stylingMode="text" hint="Zoom in" onClick={zoomIn} />
            <Button icon="fullscreen" stylingMode="text" hint="Fit to view" onClick={zoomFit} />
          </>
        ) : null}
        <span className="media-gallery-pane__sep" aria-hidden="true" />
        <Button
          icon={viewMode === 'filmstrip' ? 'mediumiconslayout' : 'smalliconslayout'}
          stylingMode="text"
          hint={viewMode === 'filmstrip' ? 'Show thumbnail grid' : 'Show filmstrip'}
          onClick={() =>
            onViewModeChange(viewMode === 'filmstrip' ? 'grid' : 'filmstrip')
          }
        />
      </div>

      {viewMode === 'filmstrip' ? (
        <div className="media-gallery-pane__filmstrip" role="list" aria-label="Image thumbnails">
          {neighbors.map((item) => {
            const active = normalizePath(item.path) === normalizePath(relativePath);
            return (
              <button
                key={item.path}
                type="button"
                role="listitem"
                className={`media-gallery-pane__thumb${active ? ' media-gallery-pane__thumb--active' : ''}`}
                style={{ width: thumbPx, height: thumbPx }}
                title={item.name}
                aria-current={active ? 'true' : undefined}
                onClick={() => onSelectPath(item.path)}
              >
                <img src={previewFileUrl(mountId, item.path)} alt="" loading="lazy" />
              </button>
            );
          })}
        </div>
      ) : (
        <div className="media-gallery-pane__grid" role="list" aria-label="Image thumbnails">
          {playlist.map((item) => {
            const active = normalizePath(item.path) === normalizePath(relativePath);
            return (
              <button
                key={item.path}
                type="button"
                role="listitem"
                className={`media-gallery-pane__thumb${active ? ' media-gallery-pane__thumb--active' : ''}`}
                style={{ width: thumbPx, height: thumbPx }}
                title={item.name}
                aria-current={active ? 'true' : undefined}
                onClick={() => onSelectPath(item.path)}
              >
                <img src={previewFileUrl(mountId, item.path)} alt="" loading="lazy" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function normalizePath(path: string): string {
  return path.replace(/\\/g, '/').replace(/^\/+/, '');
}
